import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import { AdAnalytics, AdsApiResponse } from './types';

export const AD_ANALYTICS_QUERY_KEY = 'ad-analytics';

/** Zakresy oferowane w przełączniku nad wykresem. */
export const AD_ANALYTICS_RANGES = [7, 30, 90] as const;

export const fetchAdAnalytics = async (
  adId: string,
  days: number,
  hotelId?: string,
): Promise<AdAnalytics> => {
  const res = await axiosInstance.get<AdsApiResponse<AdAnalytics>>('/v3-getAdAnalytics', {
    params: { adId, days, ...(hotelId ? { hotelId } : {}) },
  });
  const data = res.data?.data;
  if (!data || !Array.isArray(data.daily)) {
    throw new Error('Nieoczekiwany kształt odpowiedzi /v3-getAdAnalytics');
  }
  return data;
};

/**
 * Statystyki wyświetleń i kliknięć reklamy. Dane pochodzą z GA4, który
 * przelicza raporty raz na dobę — odświeżanie przy powrocie do karty tylko
 * dokładałoby zapytań, nie świeżości.
 *
 * `hotelId` zawęża raport do jednego obiektu; bez niego backend sumuje
 * wszystkie hotele, w których reklama się wyświetla.
 */
export const useGetAdAnalytics = (
  adId: string | undefined,
  days: number,
  hotelId?: string,
): UseQueryResult<AdAnalytics, AxiosError> => useQuery<AdAnalytics, AxiosError>({
  queryKey: [AD_ANALYTICS_QUERY_KEY, adId, days, hotelId ?? null],
  queryFn: () => fetchAdAnalytics(adId as string, days, hotelId),
  enabled: !!adId,
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: (failureCount, error) => {
    const status = error?.response?.status;
    if (status && status >= 400 && status < 500) return false;
    return failureCount < 2;
  },
});
